/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles.css';

import {
  Button,
  FLink,
  LoadingIndicator,
  TextInput,
  Toggler,
} from '@components';

import logo from '@assets/images/logo-sem-frase.svg';
import { useAuth } from '@pages/Shell';
import { Field, Formik } from 'formik';
import RECAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bool, object, string } from 'yup';

import { ConfiguracaoPortalService } from '@services';
import { baseCAPTCHA, baseCAPTCHAKEY } from '@services/env-variables';
import {
  HOME,
  PRIMEIRO_ACESSO,
  RECUPERAR_SENHA,
} from '../../common/utils/paths';

import { resetUser } from '../../store/user/slice';
import { getDadosPessoais, login } from '../../store/user/thunks';

// type KeyLock = 'CapsLock' | 'NumLock' | 'ScrollLock';
const useKeyLock = (key) => {
  const [toggled, setToggled] = useState(false);

  useEffect(() => {
    const onKeyDown = (event) =>
      setToggled((pToggled) => event.getModifierState?.(key) ?? pToggled);

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [key]);

  return toggled;
};

const requiredCaptcha = !!baseCAPTCHA;

const loginSchema = object({
  recaptcha: requiredCaptcha ? string().required() : string().optional(),
  matricula: string().required('A matricula é obrigatória'),
  senha: string().required('A senha é obrigatória'),
  remember: bool(),
});

export const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = useSelector((state) => state.user);
  const [erroCredenciais, setErroCredenciais] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isManutencao, setIsManutencao] = useState({});
  const [carregaTela, setCarregaTela] = useState(false);
  const capsLock = useKeyLock('CapsLock');

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const service = new ConfiguracaoPortalService();
      const response = await service.verificarManutencao();

      if (!isMounted) {
        return;
      }

      if (!window.location.origin.indexOf('adm') > -1) {
        setIsManutencao(response);
      }
      setCarregaTela(true);
    };

    run();

    return () => {
      isMounted = false;
    };
  }, []);

  const recuperaMatricula = () => {
    const matriculaLS = localStorage.getItem('LembrarMatricula');
    if (matriculaLS !== null) {
      return matriculaLS;
    }
    return '';
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setErroCredenciais(false);

    dispatch(
      login({
        matricula: values.matricula,
        senha: values.senha,
      })
    );
  };

  useEffect(() => {
    if (!user || !user.error) {
      return;
    }

    setLoading(false);
    toast.error('Ocorreu um erro. Favor entrar em contato com a Forluz.');
  }, [user]);

  useEffect(() => {
    if (!user || !user.error) {
      return;
    }

    if (user.error.includes('Usuario ou senha inválidos')) {
      setLoading(false);
      toast.error(
        'Credenciais inválidas, verifique a matrícula e/ou senha e tente novamente.'
      );
      setErroCredenciais(true);
      dispatch(resetUser());
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (!user.isLogged || !user.data.matricula || !user.data.token) {
      return;
    }

    auth.signin(user.data.matricula, () => {
      dispatch(
        getDadosPessoais({
          matricula: user.data.matricula,
          token: user.data.token,
        })
      );

      navigate(HOME, { replace: true });
    });

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth,
    dispatch,
    navigate,
    user.data.matricula,
    user.data.plano,
    user.data.token,
    user.isLogged,
  ]);

  return carregaTela ? (
    <div className="container login">
      <div className="row">
        <div className="col-12 col-sm-8 col-md-6 col-lg-5 col-xl-4 mx-auto">
          <div className="card">
            {!isManutencao.isForaDoAr ? (
              <div className="card-body">
                <img className="logo" src={logo} alt="Logo Forluz" />
                {!loading ? (
                  <Formik
                    enableReinitialize
                    initialValues={{
                      matricula: recuperaMatricula(),
                      senha: '',
                      recaptcha: '',
                      remember: !!recuperaMatricula(),
                    }}
                    validationSchema={loginSchema}
                    onSubmit={(values) => {
                      handleSubmit(values);
                    }}
                  >
                    {({
                      handleSubmit,
                      isSubmitting,
                      errors,
                      touched,
                      isValid,
                      values,
                      setFieldValue,
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Field
                          className="mb-3"
                          name="matricula"
                          type="number"
                          large
                          component={TextInput}
                          label="Matrícula"
                        />
                        <Field
                          className="mb-32"
                          name="remember"
                          label="Lembrar matrícula"
                          component={Toggler}
                        />
                        <Field
                          name="senha"
                          large
                          iconChangeType
                          component={TextInput}
                          label="Senha"
                        />
                        {values.matricula && values.senha && (
                          <RECAPTCHA
                            sitekey={baseCAPTCHAKEY}
                            render="explicit"
                            className="mt-32 mb-32 mx-sm-4"
                            onChange={(response) => {
                              setFieldValue('recaptcha', response);
                            }}
                            badge="inline"
                          />
                        )}
                        {errors.recaptcha && touched.recaptcha && (
                          <p>{errors.recaptcha}</p>
                        )}
                        {erroCredenciais && (
                          <p className="d-block text-center mt-3 ts-16 fw-bold text-red">
                            Matrícula e/ou senha inválidos!
                          </p>
                        )}

                        {capsLock && (
                          <p className="d-block text-center mt-2 mb-32 ts-16 fw-bold text-red">
                            Atenção: Caps Lock ativo
                          </p>
                        )}
                        <Button
                          className="mt-32"
                          large
                          type="submit"
                          disabled={isSubmitting || !isValid || loading}
                          onClick={handleSubmit}
                        >
                          Enviar
                        </Button>
                        <FLink
                          className="d-block text-center mt-3"
                          icon={false}
                          to={PRIMEIRO_ACESSO}
                        >
                          Primeiro acesso?
                        </FLink>
                        <FLink
                          className="d-block text-center mt-3"
                          icon={false}
                          to={RECUPERAR_SENHA}
                        >
                          Esqueceu sua senha?
                        </FLink>
                      </form>
                    )}
                  </Formik>
                ) : (
                  <LoadingIndicator />
                )}
              </div>
            ) : (
              <div className="card-body">
                <img className="logo" src={logo} alt="Logo Forluz" />
                <div className="mt-32">
                  <h1 className="ts-20 text-orange fw-bold text-center">
                    ESTAMOS EM MANUTENÇÃO
                  </h1>
                  <h3 className="ts-18 mt-32">
                    {isManutencao.MensagemForaDoAr}
                    <br />
                    <br />
                    Esta atualização não interfere nas informações
                    disponibilizadas pelo Portal.
                    <br />
                    <br />
                    Em caso de dúvidas entre em contato através do telefone 0800
                    090 9090 ou utilize nossa{' '}
                    <a href="https://institucional.forluz.org.br/CentralDeAtendimento/central-atendimento">
                      central de atendimento
                    </a>
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <LoadingIndicator />
  );
};
